import { atom } from 'jotai';
import { loadable } from 'jotai/utils';
import request from 'lib/request';
import { ALL_OPTION_ID } from './utils';
import type { InventoryAutomationStatusResponse } from './types';

// Column ids the backend can sort on. Anything outside this set is ignored so the server falls
// back to its default most-recently-recomputed-first order.
const SORTABLE_COLUMN_IDS = new Set([
  'state',
  'reason',
  'triggeredBy',
  'createdAt',
  'updatedAt',
]);

export interface SortState {
  id: string;
  desc: boolean;
}

export interface ColumnFilter {
  id: string;
  value: unknown;
}

// Filter atoms
export const hoursAtom = atom(24);
export const stateFilterAtom = atom<string>(ALL_OPTION_ID);
export const reasonFilterAtom = atom<string>(ALL_OPTION_ID);
export const triggeredByFilterAtom = atom<string>(ALL_OPTION_ID);
export const typeCurveAreaFilterAtom = atom<string>(ALL_OPTION_ID);

// Server-driven table state fed from the DataTable's onSortChange / onFilterChange callbacks.
export const sortAtom = atom<SortState | null>(null);
export const columnFiltersAtom = atom<ColumnFilter[]>([]);

// Pagination atoms
export const pageAtom = atom(1);
export const pageSizeAtom = atom(100);

// Refresh trigger atom - increment to refetch data.
const refreshCounterAtom = atom(0);

// Shared AbortController — cancelled on each new fetch so stale requests don't overwrite fresh
// results.
let activeController = new AbortController();

const inventoryAutomationStatusBaseAtom = atom(async (get) => {
  get(refreshCounterAtom);

  return await fetchAutomationStatus({
    hours: get(hoursAtom),
    stateFilter: get(stateFilterAtom),
    reasonFilter: get(reasonFilterAtom),
    triggeredByFilter: get(triggeredByFilterAtom),
    typeCurveAreaFilter: get(typeCurveAreaFilterAtom),
    sort: get(sortAtom),
    columnFilters: get(columnFiltersAtom),
    page: get(pageAtom),
    pageSize: get(pageSizeAtom),
  });
});

// Loadable wrapper for loading/error states
const inventoryAutomationStatusLoadableAtom = loadable(
  inventoryAutomationStatusBaseAtom
);

// Combined atom that provides loadable state and refetch capability
export const inventoryAutomationStatusAtom = atom(
  (get) => get(inventoryAutomationStatusLoadableAtom),
  (_get, set) => {
    set(refreshCounterAtom, (c) => c + 1);
  }
);

export interface AutomationStatusQuery {
  hours: number;
  stateFilter: string;
  reasonFilter: string;
  triggeredByFilter: string;
  typeCurveAreaFilter: string;
  sort: SortState | null;
  columnFilters: ColumnFilter[];
  page: number;
  pageSize: number;
}

// Translate the DataTable's per-column filter state into the backend query params it understands.
// Columns without a server-side filter are ignored.
function applyColumnFilters(
  query: Record<string, unknown>,
  columnFilters: ColumnFilter[]
): void {
  for (const { id, value } of columnFilters) {
    if (id === 'unitName' && typeof value === 'string' && value.trim()) {
      query.unit_name = value.trim();
    }
    if (id === 'formationNames' && typeof value === 'string' && value.trim()) {
      query.formation_name = value.trim();
    }
  }
}

// Shared fetch function used by both initial load and polling
export async function fetchAutomationStatus({
  hours,
  stateFilter,
  reasonFilter,
  triggeredByFilter,
  typeCurveAreaFilter,
  sort,
  columnFilters,
  page,
  pageSize,
}: AutomationStatusQuery): Promise<InventoryAutomationStatusResponse> {
  // Abort any in-flight request before starting a new one
  activeController.abort();
  activeController = new AbortController();

  const query: Record<string, unknown> = {
    hours,
    page,
    page_size: pageSize,
  };
  if (stateFilter !== ALL_OPTION_ID) query.state = stateFilter;
  if (reasonFilter !== ALL_OPTION_ID) query.reason = reasonFilter;
  if (triggeredByFilter !== ALL_OPTION_ID)
    query.triggered_by = triggeredByFilter;
  if (typeCurveAreaFilter !== ALL_OPTION_ID)
    query.type_curve_area_id = typeCurveAreaFilter;
  if (sort && SORTABLE_COLUMN_IDS.has(sort.id)) {
    query.sort_attribute = sort.id;
    query.sort_direction = sort.desc ? 'desc' : 'asc';
  }
  applyColumnFilters(query, columnFilters);

  const response = await request.post<
    InventoryAutomationStatusResponse,
    unknown
  >('/queries/inventory_automation_statuses', { query }, activeController);

  if (response.type === 'abort') throw new Error('Request aborted');
  if (response.type !== 'success') {
    throw new Error(
      response.type === 'fatal' ? response.message : 'Request failed'
    );
  }
  return response.data;
}

// Build a fingerprint string for cheap comparison. Only includes the fields that move while a
// recompute runs, so an unchanged payload does not re-render the table.
export function buildFingerprint(
  data: InventoryAutomationStatusResponse
): string {
  return data.results
    .map(
      (row) =>
        `${row.id}:${row.state}:${row.reason}:${row.systemWorkspaceId}:${row.updatedAt}`
    )
    .join('|');
}
